import {
  Box,
  Container,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import { ArrowLeft } from "lucide-react";

import type { GameMetadata } from "@/data/games";
import { EdgePrimitive, GlowPrimitive } from "@/design-system/primitives";
import { useI18n } from "@/i18n";
import { PageShell } from "@/shared/layout/PageShell";
import { SeoHead } from "@/shared/seo/SeoHead";
import { GlowButton } from "@/shared/ui/GlowButton";
import { NvpCard } from "@/shared/ui/NvpCard";
import { PrimitiveBadge } from "@/shared/ui/PrimitiveBadge";
import { SectionHeading } from "@/shared/ui/SectionHeading";
import { AppleStoreIcon, GooglePlayIcon } from "@/shared/ui/StoreIcons";

interface GameDetailPageProps {
  readonly game: GameMetadata;
}

const modeTones = ["blue", "pink", "yellow"] as const;

export function GameDetailPage({ game }: GameDetailPageProps) {
  const { copy, localize } = useI18n();
  const howEyebrow = game.site.howEyebrow
    ? localize(game.site.howEyebrow)
    : copy.details.howEyebrow;
  const howTitle = game.site.howTitle ? localize(game.site.howTitle) : copy.details.howTitle;
  const modesEyebrow = game.site.modesEyebrow
    ? localize(game.site.modesEyebrow)
    : copy.details.modesEyebrow;
  const modesTitle = game.site.modesTitle
    ? localize(game.site.modesTitle)
    : copy.details.modesTitle;

  return (
    <PageShell>
      <SeoHead
        title={`${localize(game.title)} - NVP Games`}
        description={localize(game.description)}
        path={`/games/${game.slug}`}
        image={game.heroImage}
      />
      <Box as="main" pt={{ base: 28, md: 32 }} pb={20} position="relative">
        <GlowPrimitive tone="pink" top="120px" right="-80px" h="280px" w="280px" />
        <GlowPrimitive tone="blue" top="360px" left="-100px" h="240px" w="240px" />
        <Container maxW="1180px" position="relative">
          <GlowButton
            as="a"
            href="/"
            variantKind="ghost"
            motion="lift"
            leftIcon={<ArrowLeft size={18} />}
            mb={8}
          >
            {copy.details.back}
          </GlowButton>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center">
            <VStack align="start" spacing={6}>
              <PrimitiveBadge tone="pink">{localize(game.genre)}</PrimitiveBadge>
              <Box>
                <Heading as="h1" fontSize={{ base: "4rem", md: "6rem" }} lineHeight={0.94}>
                  {localize(game.title)}
                </Heading>
                <Text color="whiteAlpha.800" fontSize={{ base: "lg", md: "xl" }} mt={4}>
                  {localize(game.tagline)}
                </Text>
              </Box>
              <Text maxW="640px" color="whiteAlpha.800" fontSize="lg" lineHeight={1.8}>
                {localize(game.description)}
              </Text>
              <HStack flexWrap="wrap" gap={3}>
                {game.links.appStore ? (
                  <GlowButton
                    as="a"
                    href={game.links.appStore}
                    target="_blank"
                    rel="noreferrer"
                    leftIcon={<AppleStoreIcon size={18} />}
                    glow="pink"
                  >
                    {copy.featured.appStore}
                  </GlowButton>
                ) : null}
                {game.links.googlePlay ? (
                  <GlowButton
                    as="a"
                    href={game.links.googlePlay}
                    target="_blank"
                    rel="noreferrer"
                    leftIcon={<GooglePlayIcon size={18} />}
                    glow="blue"
                  >
                    {copy.featured.googlePlay}
                  </GlowButton>
                ) : null}
              </HStack>
            </VStack>

            <Box>
              {game.heroScreenshots ? (
                <SimpleGrid columns={2} spacing={4} maxW="480px" mx="auto">
                  {game.heroScreenshots.map((src) => (
                    <Image
                      key={src}
                      src={src}
                      alt={localize(game.gallery.find((image) => image.src === src)?.alt ?? game.title)}
                      borderRadius="8px"
                      w="100%"
                      boxShadow="0 28px 80px rgba(0,0,0,0.34)"
                    />
                  ))}
                </SimpleGrid>
              ) : (
                <Image
                  src={game.heroImage}
                  alt={localize(game.title)}
                  borderRadius="8px"
                  w="100%"
                  boxShadow="0 28px 80px rgba(0,0,0,0.34)"
                />
              )}
            </Box>
          </SimpleGrid>

          <Box as="section" py={{ base: 14, md: 20 }}>
            <SectionHeading
              eyebrow={howEyebrow}
              title={howTitle}
              align="center"
            />
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mt={10}>
              {game.site.howToPlay.map((step, index) => (
                <NvpCard
                  key={localize(step.title)}
                  tone={modeTones[index % modeTones.length]}
                  p={5}
                  minH="210px"
                  interactive
                >
                  <PrimitiveBadge tone={modeTones[index % modeTones.length]}>
                    {String(index + 1).padStart(2, "0")}
                  </PrimitiveBadge>
                  <Heading as="h3" fontSize="xl" mt={5} mb={3}>
                    {localize(step.title)}
                  </Heading>
                  <Text color="whiteAlpha.700">{localize(step.body)}</Text>
                  {step.primitive === "edge" ? (
                    <EdgePrimitive tone="blue" left="24px" right="24px" bottom="34px" />
                  ) : (
                    <GlowPrimitive tone="yellow" right="18px" bottom="18px" h="92px" w="92px" />
                  )}
                </NvpCard>
              ))}
            </SimpleGrid>
          </Box>

          <Box as="section" py={{ base: 8, md: 14 }}>
            <SectionHeading
              eyebrow={modesEyebrow}
              title={modesTitle}
            />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} mt={10}>
              {game.site.modes.map((mode, index) => (
                <NvpCard
                  key={mode.id}
                  tone={modeTones[index % modeTones.length]}
                  p={6}
                  interactive
                >
                  <PrimitiveBadge tone={modeTones[index % modeTones.length]}>
                    {localize(mode.tag)}
                  </PrimitiveBadge>
                  <Heading as="h3" fontSize="2xl" mt={5} mb={3}>
                    {localize(mode.title)}
                  </Heading>
                  <Text color="whiteAlpha.700">{localize(mode.body)}</Text>
                </NvpCard>
              ))}
            </SimpleGrid>
          </Box>

          {game.site.sections.map((section, index) => {
            const sectionImages = section.images ?? (section.image ? [section.image] : []);

            return (
              <SimpleGrid
                as="section"
                key={section.id}
                columns={{ base: 1, lg: 2 }}
                spacing={{ base: 8, lg: 12 }}
                alignItems="center"
                py={{ base: 12, md: 18 }}
              >
                <Box order={{ base: 1, lg: index % 2 === 0 ? 1 : 2 }}>
                  <SectionHeading
                    eyebrow={localize(section.eyebrow)}
                    title={localize(section.title)}
                    body={localize(section.body)}
                  />
                </Box>
                {sectionImages.length > 0 ? (
                  <SimpleGrid
                    columns={2}
                    spacing={{ base: 3, md: 4 }}
                    justifySelf={{ base: "stretch", lg: "end" }}
                    maxW={{ base: "100%", sm: "560px", lg: "520px" }}
                    order={{ base: 2, lg: index % 2 === 0 ? 2 : 1 }}
                  >
                    {sectionImages.map((image) => (
                      <Box
                        key={image}
                        overflow="hidden"
                        border="2px solid transparent"
                        borderRadius="8px"
                        bg={
                          index % 2 === 0
                            ? "linear-gradient(#000000, #000000) padding-box, linear-gradient(135deg, rgba(55,213,255,0.82), rgba(155,92,255,0.58), rgba(255,79,216,0.72)) border-box"
                            : "linear-gradient(#000000, #000000) padding-box, linear-gradient(135deg, rgba(255,79,216,0.82), rgba(155,92,255,0.58), rgba(55,213,255,0.72)) border-box"
                        }
                      >
                        <Image
                          src={image}
                          alt={localize(section.title)}
                          display="block"
                          w="100%"
                          h="auto"
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : null}
              </SimpleGrid>
            );
          })}
        </Container>
      </Box>
    </PageShell>
  );
}
